// Package imports:
import React, { useEffect, useMemo, useRef } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import Highcharts from 'highcharts/highstock';
// Style imports:
// import './TickDataChartRange.scss';
// Action imports:
import { setSelectedTick } from '../../redux-store/actions/dashboardActions';
// Type imports:
import { RootState } from '../../redux-store/reducers';
import { TickData, WithBook } from '../../types/api/Types';
import { PublicOrderByLevelTickData, PublicOrderTickData, PublicTradeTickData } from '../../types/api/PublicTypes';
import { AlertTickData } from '../../types/api/AlertTypes';
import { NewsTickData } from '../../types/api/NewsTypes';
// Svg imports:
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faNewspaper } from '@fortawesome/free-solid-svg-icons';
import ReactDOMServer from 'react-dom/server';
import { createFontAwesomeIcon } from '../../services/iconUtils';
import { getUniqueTickId } from '../../services/utils';

type HighchartPointWithTick = Highcharts.PointOptionsObject & {
    tick: TickData
}

interface IOwnProps {
    symbol: string,
    lastPrice: number | null
    sortedPublicOrderTickData: WithBook<PublicOrderTickData>[],
    sortedPublicOrdersByFirstLevelTickData: WithBook<PublicOrderByLevelTickData>[],
    sortedPublicTradeTickData: PublicTradeTickData[],
    sortedAlertTickData: AlertTickData[],
    sortedNewsTickData: NewsTickData[],
    checkedAdditionalTickIds?: string[];
};
type ReduxProps = ConnectedProps<typeof connector>;
type Props = IOwnProps & ReduxProps;

const TickDataHighChart: React.FC<Props> = ({
    symbol,
    currentTradingDay,
    lastPrice,
    sortedPublicOrderTickData,
    sortedPublicOrdersByFirstLevelTickData,
    sortedPublicTradeTickData,
    sortedAlertTickData,
    sortedNewsTickData,
    selectedTick,
    setSelectedTick,
    checkedAdditionalTickIds = []
}) => {
    // Refs:
    const chartInstanceRef = useRef<Highcharts.StockChart | null>(null);
    const chartElementRef = useRef<HTMLDivElement | null>(null);
    const prevCheckedIdsRef = useRef<string[]>([]);
    // Chart-related variables. We store them here so that we can re-render smoothly.
    // However we don't want them in a state, as we don't want to force re-render the component.
    const selectedRangeRef = useRef<Highcharts.AxisSetExtremesEventObject | null>(null);
    const visibleSeriesMapRef = useRef<{ [T in string]?: boolean }>({});

    const events = useMemo(() => ({
        click: function(e: Highcharts.SeriesClickEventObject) {
            // @ts-ignore
            const tick = e?.point?.tick ?? null;
            setSelectedTick(tick);
        },
        legendItemClick: function(e: Highcharts.SeriesLegendItemClickEventObject) {
            const visibleSeriesMap = visibleSeriesMapRef.current;
            visibleSeriesMapRef.current = {
                ...visibleSeriesMap,
                [e.target.name]: !e.target.visible
            }
        }
    }), [ setSelectedTick ]);

    useEffect(() => {
        selectedRangeRef.current = null;
        visibleSeriesMapRef.current = {};
    }, [ symbol, currentTradingDay ]);

    useEffect(() => {
        const bidData: HighchartPointWithTick[] = [];
        const askData: HighchartPointWithTick[] = [];

        // By order.
        for (let { tick, book } of sortedPublicOrderTickData) {
            // Bid.
            if (tick.side === 'bid') {
                if (tick.timestamp !== null && book.bookBidPrice !== null) {
                    const lastBidDatum: HighchartPointWithTick | null = bidData[bidData.length - 1] ?? null;
                    if (
                        lastBidDatum !== null
                        && lastBidDatum.tick.timestamp === tick.timestamp
                    ) {
                        if (
                            lastBidDatum.y !== null
                            && lastBidDatum.y !== undefined
                            && lastBidDatum.y < book.bookBidPrice
                        ) {
                            lastBidDatum.y = book.bookBidPrice;
                        }
                    } else {
                        bidData.push({
                            tick: tick,
                            x: new Date(tick.timestamp).getTime(),
                            y: book.bookBidPrice
                        });
                    }
                }
            }
            // Ask.
            else {
                if (tick.timestamp !== null && book.bookAskPrice !== null) {
                    const lastAskDatum: HighchartPointWithTick | null = askData[askData.length - 1] ?? null;
                    if (
                        lastAskDatum !== null
                        && lastAskDatum.tick.timestamp === tick.timestamp
                    ) {
                        if (
                            lastAskDatum.y !== null
                            && lastAskDatum.y !== undefined
                            && lastAskDatum.y > book.bookAskPrice
                        ) {
                            lastAskDatum.y = book.bookAskPrice;
                        }
                    } else {
                        askData.push({
                            tick: tick,
                            x: new Date(tick.timestamp).getTime(),
                            y: book.bookAskPrice,
                        });
                    }
                }
            }
        }
        // By level.
        for (let { tick, book } of sortedPublicOrdersByFirstLevelTickData) {
            // Bid.
            if (tick.side === 'bid') {
                if (tick.timestamp !== null && book.bookBidPrice !== null) {
                    bidData.push({
                        tick: tick,
                        x: new Date(tick.timestamp).getTime(),
                        y: book.bookBidPrice
                    });
                }
            }
            // Ask.
            else {
                if (tick.timestamp !== null && book.bookAskPrice !== null) {
                    askData.push({
                        tick: tick,
                        x: new Date(tick.timestamp).getTime(),
                        y: book.bookAskPrice,
                    });
                }
            }
        }

        const standardTradeData: HighchartPointWithTick[] = [];
        const nonStandardTradeData: HighchartPointWithTick[] = [];
        for (let tick of sortedPublicTradeTickData) {
            if (tick.timestamp !== null && tick.price !== null) {
                if (tick.trade_class === 'standard') {
                    standardTradeData.push({
                        tick: tick,
                        x: new Date(tick.timestamp).getTime(),
                        y: tick.price
                    });
                }
                if (tick.trade_class === 'non_standard') {
                    nonStandardTradeData.push({
                        tick: tick,
                        x: new Date(tick.timestamp).getTime(),
                        y: tick.price
                    });
                }
            }
        }

        const alertsData: HighchartPointWithTick[] = [];
        for (let tick of sortedAlertTickData) {
            if (tick.timestamp !== null) {
                alertsData.push({
                    tick: tick,
                    x: new Date(tick.timestamp).getTime(),
                    y: lastPrice || undefined
                });
            }
        }

        const newsData: HighchartPointWithTick[] = [];
        for (let tick of sortedNewsTickData) {
            if (tick.timestamp !== null) {
                newsData.push({
                    tick: tick,
                    x: new Date(tick.timestamp).getTime(),
                    y: lastPrice || undefined
                });
            }
        }

        // const combinedTicks = [
        //     ...bidData,
        //     ...askData,
        //     ...standardTradeData,
        //     ...nonStandardTradeData,
        //     ...alertsData,
        //     ...newsData
        // ];
        // let lowestX: number | undefined = undefined;
        // let highestX: number | undefined = undefined;
        // for (let tick of combinedTicks) {
        //     if (lowestX === undefined) {
        //         lowestX = tick.x;
        //         continue;
        //     }
        //     if (highestX === undefined) {
        //         highestX = tick.x;
        //         continue;
        //     }
        //     if (tick.x === undefined) return;

        //     if (tick.x < lowestX) lowestX = tick.x;
        //     if (tick.x > highestX) highestX = tick.x;
        // }
        const bellIcon = ReactDOMServer.renderToString(<FontAwesomeIcon icon={faBell} />);
        const newsIcon = ReactDOMServer.renderToString(<FontAwesomeIcon icon={faNewspaper} />);

        // Create a custom symbol
        Highcharts.SVGRenderer.prototype.symbols.roundedSquare = function (x: number, y: number, w: number, h: number, options: any) {
            const r = (options && options.r) || 4;
            return [
                'M', x + r, y,
                'L', x + w - r, y,
                'C', x + w, y, x + w, y, x + w, y + r,
                'L', x + w, y + h - r,
                'C', x + w, y + h, x + w, y + h, x + w - r, y + h,
                'L', x + r, y + h,
                'C', x, y + h, x, y + h, x, y + h - r,
                'L', x, y + r,
                'C', x, y, x, y, x + r, y,
                'Z'
            ];
        };

        chartInstanceRef.current?.destroy();
        const element = chartElementRef.current;
        if (!element) return;


        chartInstanceRef.current = Highcharts.stockChart(element, {
            chart: {
                zooming: {type: 'x'},
                height: '300px'
            },
            accessibility: {
                enabled: false
            },
            xAxis: {
                id: 'xAxis1',
                ordinal: false,
                startOnTick: true,
                endOnTick: true,
                min: selectedRangeRef.current?.min ?? undefined,
                max: selectedRangeRef.current?.max ?? undefined,
                events: {
                    setExtremes: function(e) {
                        selectedRangeRef.current = e;
                    },
                }
            },
            yAxis: {
                id: 'yAxis1',
                labels: {
                    align: 'left'
                },
                resize: {
                    enabled: true
                },
                crosshair: true
            },
            legend: {
                enabled: true,
                layout: 'vertical',
                align: 'right',
                verticalAlign: 'middle'
            },
            
            series: [{
                // =================== Standard Trades on the chart ===================
                type: 'line',
                id: 'standardTrades',
                name: `${symbol} Standard Trade`,
                visible: visibleSeriesMapRef.current?.[`${symbol} Standard Trade`] ?? true,
                color: '#292A2E',
                lineWidth: 0, // We want a scatterplot, but `type: "scatter"` has issues
                animation: false,
                marker: {
                    enabled: true,
                    symbol: 'roundedSquare',
                    lineColor: '#292A2E',
                    lineWidth: 2.5,
                    radius: 7,
                    fillColor: '#FFFFFF'
                },
                events: events,
                data: standardTradeData
            }, {
                // =================== Non Standard Trades on the chart ===================
                type: 'line',
                id: 'nonStandardTrades',
                name: `${symbol} Non-Standard Trade`,
                visible: visibleSeriesMapRef.current?.[`${symbol} Non-Standard Trade`] ?? true,
                lineWidth: 0,
                color: '#292A2E',
                animation: false,
                marker: {
                    enabled: true,
                    symbol: 'roundedSquare',
                    lineColor: '#000000',
                    lineWidth: 1,
                    radius: 7,
                    fillColor: '#292A2E'
                },
                events: events,
                data: nonStandardTradeData
            }, {
                // =================== Bids on the chart ===================
                type: 'line',
                id: 'Bids',
                step: 'left',
                showInNavigator: true,
                name: `${symbol} Bids`,
                visible: visibleSeriesMapRef.current?.[`${symbol} Bids`] ?? true,
                color: '#01B1A7',
                animation: false,
                marker: {
                    enabled: true,
                    radius: 5
                },
                events: events,
                data: bidData
            }, {
                // =================== Asks on the chart ===================
                type: 'line',
                id: 'Asks',
                step: 'left',
                showInNavigator: true,
                name: `${symbol} Asks`,
                visible: visibleSeriesMapRef.current?.[`${symbol} Asks`] ?? true,
                color: '#EA5F5F',
                animation: false,
                marker: {
                    symbol: 'circle',
                    enabled: true,
                    radius: 5
                },
                events: events,
                data: askData
            }, {
                // =================== News on the chart ===================
                type: 'line',
                id: 'News',
                name: `${symbol} News`,
                visible: visibleSeriesMapRef.current?.[`${symbol} News`] ?? true,
                color: '#bce085',
                lineWidth: 0,
                animation: false,
                marker: {
                    enabled: true,
                    symbol: 'url(data:image/svg+xml;base64,' + btoa(createFontAwesomeIcon(newsIcon, "#292A2E", 20)) + ')',
                    lineColor: '#000000',
                    lineWidth: 1,
                    radius: 4,
                    fillColor: '#bce085'
                },
                events: events,
                data: newsData
            }, {
                // =================== Alerts on the chart ===================
                type: 'line',
                id: 'Alerts',
                name: `${symbol} Alerts`,
                visible: visibleSeriesMapRef.current?.[`${symbol} Alerts`] ?? true,
                color: '#e8c2ab',
                lineWidth: 0,
                animation: false,
                marker: {
                    enabled: true,
                    symbol: 'url(data:image/svg+xml;base64,' + btoa(createFontAwesomeIcon(bellIcon, "#FFB31F", 18)) + ')',
                    lineColor: '#000000',
                    lineWidth: 1,
                    radius: 5,
                    fillColor: '#e8c2ab'
                },
                events: events,
                data: alertsData
            }],
            rangeSelector: {
                allButtonsEnabled: true,
                inputEnabled: false,
                buttons: [{
                    type: 'hour',
                    count: 1,
                    text: '1h',
                    // events: {
                    //     click: function(e) {
                    //         console.log('1 hour')
                    //         console.log(e)
                    //     }
                    // }
                }, {
                    type: 'hour',
                    count: 4,
                    text: '4h',
                    // events: {
                    //     click: function(e) {
                    //         console.log('4 hour')
                    //         console.log(e)
                    //     }
                    // }
                }, {
                    type: 'all',
                    text: 'Day'
                }],
                selected: 2 // Range set to full day by default
            }
            // Empty callback function at end to force overload.
        }, () => {});
    }, [
        symbol,
        currentTradingDay,
        lastPrice,
        sortedPublicOrderTickData,
        sortedPublicOrdersByFirstLevelTickData,
        sortedPublicTradeTickData,
        sortedAlertTickData,
        sortedNewsTickData,
        events
    ]);

    // Plot line for selectedTick.
    useEffect(() => {
        // Remove plot line in case it exists.
        chartInstanceRef.current?.xAxis?.[0]?.removePlotLine('plotline');
        // Add plotline for selectedTick.
        if (selectedTick !== null && selectedTick.timestamp !== null) {
            chartInstanceRef.current?.xAxis?.[0]?.addPlotLine({
                id: 'plotline',
                color: '#99999999',
                width: 3,
                value: new Date(selectedTick.timestamp).getTime(),
                zIndex: 2
            });
        }
    }, [ selectedTick ]);

    // Plot lines for checkedAdditionalTickIds
    useEffect(() => {
        // Remove plot lines for previously checked IDs
        if (chartInstanceRef.current?.xAxis?.[0]) {
            const axis = chartInstanceRef.current.xAxis[0];
            prevCheckedIdsRef.current.forEach((tickId) => {
                axis.removePlotLine(`checked-${tickId}`);
            });
        }
        // Add new plot lines for each checked tick id
        checkedAdditionalTickIds.forEach((tickId) => {
            let foundTick: any = null;
            [
                ...sortedPublicOrderTickData.map(tb => tb.tick),
                ...sortedPublicOrdersByFirstLevelTickData.map(tb => tb.tick),
                ...sortedPublicTradeTickData,
                ...sortedAlertTickData,
                ...sortedNewsTickData
            ].forEach(t => {
                if (getUniqueTickId(t) === tickId) foundTick = t;
            });
            if (foundTick && foundTick.timestamp) {
                chartInstanceRef.current?.xAxis?.[0]?.addPlotLine({
                    value: new Date(foundTick.timestamp).getTime(),
                    color: '#FFB31F',
                    width: 2,
                    id: `checked-${tickId}`,
                    zIndex: 10
                });
            }
        });
        // Update previous checked IDs
        prevCheckedIdsRef.current = [...checkedAdditionalTickIds];
    }, [checkedAdditionalTickIds]);

    return (
        <div id='TickDataHighChart'>
            <div
                ref={chartElementRef}
                id='TickDataChart'
            >
                Loading...
            </div>
        </div>
    );
}

function mapStateToProps({ dashboardReducer }: RootState) {
    const { selectedTick, currentTradingDay, checkedAdditionalTickIds } = dashboardReducer;
    return {
        selectedTick,
        currentTradingDay,
        checkedAdditionalTickIds
    }
}

const connector = connect(mapStateToProps, { setSelectedTick });
export default connector(TickDataHighChart);
